import IConfig from 'app/config/Config'
import { fetchCmsUser } from 'app/lib/fetch-cms-user'
import { getMockExternalId, isMockAuthToken } from 'app/lib/mock-auth'

import { UserStore } from '..'
import {
  oneYearInMilliseconds,
  fifteenMinsInMilliseconds,
} from './token-expiry-determiner'

const fetchUserWithToken = async (
  config: IConfig,
  accessToken: string,
  externalId?: string
) => {
  let cmsUrl = config.webCmsUrl || config.cmsUrl

  // if we are server side, use the cms url
  if (typeof window === 'undefined') {
    cmsUrl = config.cmsUrl
  }

  return fetchCmsUser(accessToken, {
    cmsUrl,
    externalId,
    timeoutMs: config.timeout,
  })
}

const retrieveAccessTokenAndUser = async (
  store: UserStore,
  config: IConfig,
  params: any
) => {
  const mockAccessToken = store.auth?.accessToken
  const mockExternalId = getMockExternalId(mockAccessToken)
  const isMockRefresh =
    params?.grant_type === 'refresh_token' && isMockAuthToken(mockAccessToken)

  if (isMockRefresh && mockAccessToken) {
    const user = await fetchUserWithToken(
      config,
      mockAccessToken,
      mockExternalId
    )

    return {
      auth: {
        accessToken: mockAccessToken,
        accessTokenExpiry:
          store.auth?.accessTokenExpiry || Date.now() + oneYearInMilliseconds,
        refreshToken: store.auth?.refreshToken || mockAccessToken,
        refreshTokenExpiry:
          store.auth?.refreshTokenExpiry || Date.now() + oneYearInMilliseconds,
      },
      user,
    }
  }

  // OAuth token exchange - use JSON to match web callback
  const requestBody = JSON.stringify(params)

  const response = await fetch(`${config.memberfulOauthUrl}/token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: requestBody,
  })

  if (!response.ok) {
    const errorText = await response.text()
    console.error(
      '[retrieveAccessTokenAndUser] Token exchange failed:',
      response.status,
      errorText
    )
    console.error('[retrieveAccessTokenAndUser] Full error details:', {
      status: response.status,
      statusText: response.statusText,
      error: errorText,
      code_length: params.code?.length,
      code_verifier_length: params.code_verifier?.length,
      redirect_uri: params.redirect_uri,
      client_id: params.client_id,
    })
    throw new Error(`Token exchange failed: ${response.status} ${errorText}`)
  }

  const body = await response.json()

  const {
    access_token: accessToken,
    refresh_token: refreshToken,
    expires_in,
  } = body

  if (!accessToken) {
    console.error(
      '[retrieveAccessTokenAndUser] No access_token in response:',
      body
    )
    throw new Error('No access_token in token exchange response')
  }

  const token = accessToken

  try {
    const user = await fetchUserWithToken(config, token)

    const accessTokenExpiry = Date.now() + expires_in * 1000

    // difference between now and 15 minutes
    const timeDifference =
      fifteenMinsInMilliseconds - (accessTokenExpiry - Date.now())

    const refreshTokenExpiry =
      Date.now() + oneYearInMilliseconds - timeDifference

    const authData = {
      accessToken,
      accessTokenExpiry,
      refreshToken,
      refreshTokenExpiry,
    }

    if (params.refresh_token) {
      authData.refreshTokenExpiry = refreshTokenExpiry
      authData.refreshToken = params.refresh_token
    }

    return {
      auth: authData,
      user,
    }
  } catch (error) {
    console.error(
      '[retrieveAccessTokenAndUser] Error fetching user data:',
      error
    )
    // Re-throw with more context
    throw error
  }
}

export default retrieveAccessTokenAndUser
